import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Lock, Loader2, XCircle, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Money } from "@/components/kit";
import { LangToggle } from "@/components/layout/AppShell";
import { Logo } from "@/components/Logo";
import { dmy, errMsg } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/pay`;
const BRAND = { konnect: "bg-[#5A3FD6]", flouci: "bg-[#14B57A]" };

const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>;

const Done = ({ data }) => {
  const { t } = useApp();
  return (
    <div className="space-y-3 text-center" data-testid="pay-success">
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
      <p className="text-lg font-semibold text-slate-900">{t("pay_done")}</p>
      {data.payment && <p className="text-sm text-slate-500">{t("pay_receipt")} <span className="num font-semibold">{data.payment.number}</span> · <Money v={data.payment.amount} /></p>}
      {data.payment?.receipt_sent_to && <p className="text-xs text-slate-400" data-testid="pay-receipt-sent">{t("receipt_sent_to")} {data.payment.receipt_sent_to}</p>}
      <a href={`${API}/${data.token}/receipt?lang=${data.lang}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#E30613] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#B8000F]" data-testid="pay-receipt-download"><FileDown className="h-4 w-4" />{t("receipt_download")}</a>
    </div>
  );
};

export default function PayPage() {
  const { token } = useParams();
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [qs] = useSearchParams();
  const load = () => axios.get(`${API}/${token}`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    if (qs.get("return") !== "1") { load(); return; }
    let n = 0;
    setBusy(true);
    const check = async () => {
      try {
        const { data: v } = await axios.post(`${API}/${token}/verify`);
        if (v.state === "pending" && n++ < 6) { setTimeout(check, 3000); return; }
        if (v.state === "failed" || qs.get("failed")) setErr(t("pay_failed"));
      } catch (e) { setErr(errMsg(e)); }
      setBusy(false); load();
    };
    check();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const checkout = async () => {
    setBusy(true); setErr("");
    try { const { data: r } = await axios.post(`${API}/${token}/checkout`); window.location.assign(r.pay_url); } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  const pay = async (outcome) => {
    setBusy(true); setErr("");
    try { await axios.post(`${API}/${token}/simulate`, { outcome }); await load(); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const d = data?.document;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b bg-white px-6 py-3"><Logo size={30} /><LangToggle /></header>
      <main className="mx-auto max-w-lg p-6">
        {!data && !err && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {!data && err && <p className="py-20 text-center text-sm text-red-600" data-testid="pay-error">{err}</p>}
        {data && (
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm fade-up" data-testid="pay-card">
            <div className="flex items-center gap-3 border-b p-6">
              {data.company.has_logo && <img src={`${API}/${token}/logo`} alt="" className="h-10 w-10 rounded object-contain" />}
              <div><div className="font-semibold text-slate-900" data-testid="pay-company">{data.company.name}</div><div className="num text-xs text-slate-400">MF {data.company.matricule_fiscal}</div></div>
            </div>
            <div className="p-6">
              <div className="eyebrow">{t(`type_${d.type}`)}</div>
              <div className="num text-2xl font-bold text-slate-900" data-testid="pay-number">{d.number}</div>
              <div className="mt-3 divide-y">
                <Row k={t("partner")} v={d.partner_name} /><Row k={t("date")} v={dmy(d.date)} /><Row k={t("due_date")} v={dmy(d.due_date)} />
                <Row k={t("total_ttc")} v={<Money v={d.ttc} cur={d.currency} />} />
              </div>
              <div className="mt-4 flex items-end justify-between rounded-xl bg-slate-900 p-4 text-white">
                <span className="text-xs uppercase tracking-wider text-slate-400">{t("pay_amount")}</span>
                <span className="text-2xl font-bold" data-testid="pay-due"><Money v={data.status === "paid" ? 0 : d.due} cur={d.currency} /></span>
              </div>
              <div className="mt-6">
                {data.status === "paid" ? <Done data={{ ...data, token, lang }} /> : data.status !== "pending" ? <p className="text-center text-sm text-slate-500">{t("pay_closed")}</p> : (
                  <div className="space-y-3">
                    <Button disabled={busy} onClick={() => (data.simulated ? pay("success") : checkout())} className={`h-12 w-full text-base text-white hover:opacity-90 ${BRAND[data.provider]}`} data-testid="pay-submit">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4 me-2" />}{t("pay_with")} {data.provider_name}
                    </Button>
                    {data.simulated && <button onClick={() => pay("failed")} disabled={busy} className="w-full text-xs text-slate-400 underline" data-testid="pay-simulate-fail">{t("pay_sim_fail")}</button>}
                    {err && <p className="flex items-center justify-center gap-1 text-sm text-red-600" data-testid="pay-error"><XCircle className="h-4 w-4" />{err}</p>}
                  </div>
                )}
              </div>
              {data.simulated ? <p className="mt-6 text-center text-[11px] text-orange-700"><span className="rounded bg-orange-100 px-1.5 font-bold">{t("simulated")}</span> {t("pay_sim_note")}</p>
                : <p className="mt-6 flex items-center justify-center gap-1 text-center text-[11px] text-slate-500" data-testid="pay-secure-note"><Lock className="h-3 w-3" />{t("pay_secure_note")} {data.provider_name}{data.environment === "test" ? ` · ${t("op_sandbox")}` : ""}</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
